import { inject, Injectable, Injector } from '@angular/core';

import {
  BaseFieldRule,
  CustomDataAdditionalDataModel,
  FieldDefinitionModel,
  FieldType,
  FieldVisibility,
  ObjectDefinitionHelpers,
  ObjectDefinitionModel,
  ProxyService,
} from '@seahorse/domain';
import { IdentityServiceBase } from '@seahorse/auth';
import {
  FormModel,
  FormField,
  FormFieldValidation,
  IncrementValuesListItem,
} from './form.model';
import { FormService } from './form.service';
import { FieldFormValue } from './field-form.directive';
import { Observable } from 'rxjs';
import { ModalOptions } from '../modal/modal.model';

@Injectable({
  providedIn: 'root',
})
export class CustomDataFormService {
  private _identityService = inject(IdentityServiceBase);
  private _fieldTypeRules: { [key: number]: BaseFieldRule };

  constructor(
    private _globalCache: ProxyService,
    private _formService: FormService,
    private _objectDefinitionHelpers: ObjectDefinitionHelpers
  ) {
    this.setFieldTypeRules();
  }

  private setFieldTypeRules() {
    const fieldTypeDefinitions = this._globalCache.fieldTypeDefinitions;

    if (!fieldTypeDefinitions) {
      this._fieldTypeRules = {};
      return;
    }

    this._fieldTypeRules = this._globalCache.fieldTypeDefinitions.reduce(
      (acc, cur) => {
        acc[cur.id] = cur.rule;
        return acc;
      },
      {}
    );
  }

  openFormModal(
    definition: ObjectDefinitionModel,
    fields: string[],
    value?: any,
    injector?: Injector
  ) {
    const form = this.getFormModel(definition, fields, value);
    return this._formService.openFormModal(form, definition.name, injector);
  }

  openFieldModal(
    definition: ObjectDefinitionModel,
    field: string,
    value: any,
    injector?: Injector,
    formatting?: any,
    modalOptions?: ModalOptions
  ): Observable<FieldFormValue> {
    const form = this.getFieldModel(definition, field, value, formatting);
    return this._formService.openFieldModal(form, injector, null, modalOptions);
  }

  private isNewObject(objectValue: any): boolean {
    if (!objectValue) return true;
    return !(objectValue.id || objectValue.__Id || objectValue.__Key);
  }

  getFormModel(
    definition: ObjectDefinitionModel,
    fields: string[],
    value = null,
    publicOnly = true,
    defaultFields = true
  ): FormModel {
    const fieldDefinitions = ObjectDefinitionModel.getAllFieldDefinitions(
      definition,
      false,
      publicOnly
        ? [FieldVisibility.Public]
        : [FieldVisibility.Public, FieldVisibility.Private]
    ).filter(
      (def) =>
        def.fieldType !== FieldType.AutoNumbering &&
        (!def.isReadOnly ||
          (def.isReadOnly &&
            def.isRequired &&
            (def.defaultValue == null || def.defaultValue === ''))) &&
        (fields?.length > 0 ? fields?.includes(def.systemCode) : defaultFields)
    );

    const formFields = this.getFormFields(fieldDefinitions, value, definition);

    const form: FormModel = {
      fields: formFields,
      value,
    };

    return form;
  }

  getFieldModel(
    definition: ObjectDefinitionModel,
    field: string,
    value = null,
    formatting?: any
  ): FormModel {
    const fieldDefinition = ObjectDefinitionModel.getAllFieldDefinitions(
      definition
    ).find((item) => item.systemCode === field);
    const formFields = this.getFormField(
      fieldDefinition,
      value,
      formatting,
      definition
    );

    const form: FormModel = {
      fields: [formFields],
      value: { [field]: value },
    };

    return form;
  }

  private getFormFields(
    fieldDefinitions: FieldDefinitionModel[],
    value = null,
    definition?: ObjectDefinitionModel
  ): FormField[] {
    return fieldDefinitions.map((field) =>
      this.getFormField(field, value, undefined, definition)
    );
  }

  private getFormField(
    field: FieldDefinitionModel,
    value: any,
    formatting?: any,
    definition?: ObjectDefinitionModel
  ): FormField {
    const additionalData = FieldDefinitionModel.getAdditionalData(field);
    const isNew = this.isNewObject(value);
    const immutable =
      this._objectDefinitionHelpers.isImmutableAfterCreate(field);

    return {
      key: field.systemCode,
      name: field.name,
      groupName: field.groupName,
      type: field.fieldType,
      isRequired: field.isRequired,
      isDisabled: immutable
        ? !isNew
        : field.isReadOnly ||
          FieldDefinitionModel.isReadOnlyByObjectState(
            field,
            value?.__ObjectState
          ),
      validation: this.getFieldValidation(field),
      options: {
        fieldDefinition: field,
        fieldCode: field.fieldCode,
        formatting:
          value?.__DataFieldFormatting?.[field.systemCode] ?? formatting,
        mode: additionalData?.mode?.edit,
        listItems: field.fieldRule?.['items']?.length
          ? field.fieldRule['items']
          : undefined,
        appendText: field?.fieldRule?.['valueDescriptor'],
        incrementValuesList: this.getIncrementValuesList(additionalData),
      },
      connectedFields:
        this._identityService.hasFeature('propagateConnectedFields') &&
        definition
          ? this._objectDefinitionHelpers.getConnectedFields(
              field.systemCode,
              definition?.editConfig?.connectedFields
            )
          : undefined,
    };
  }

  private getFieldValidation(
    field: FieldDefinitionModel
  ): Partial<FormFieldValidation> {
    switch (field.fieldType) {
      case FieldType.SingleLineText:
      case FieldType.MultiLineText:
        return {
          maxLength: this.maxLength(field),
          minLength: this.minLength(field),
          pattern: this.pattern(field),
        };

      case FieldType.Date:
      case FieldType.DateTime:
        if (!field.fieldRule) {
          return undefined;
        }

        return {
          format: BaseFieldRule.date(field.fieldRule).format,
          minDate: BaseFieldRule.date(field.fieldRule).minDate,
          maxDate: BaseFieldRule.date(field.fieldRule).maxDate,
        };
      case FieldType.Time:
        if (!field.fieldRule) {
          return {
            format: 'HH:mm',
          };
        }

        return {
          format: BaseFieldRule.time(field.fieldRule).format,
          minTime: BaseFieldRule.time(field.fieldRule).minTime,
          maxTime: BaseFieldRule.time(field.fieldRule).maxTime,
        };
      case FieldType.Decimal:
        if (!field.fieldRule) {
          return {
            numDecimals: 2,
          };
        }

        return {
          numDecimals: BaseFieldRule.decimal(field.fieldRule).digits,
        };
      case FieldType.Currency:
        if (!field.fieldRule) {
          return {
            numDecimals: 2,
          };
        }

        return {
          numDecimals: BaseFieldRule.decimal(field.fieldRule).digits,
          currencyCodes: field.fieldRule['currencyCodes'],
        };
      case FieldType.List:
        if (!field.fieldRule) {
          return undefined;
        }

        return {
          max: BaseFieldRule.list(field.fieldRule).max,
          mustMatch: BaseFieldRule.list(field.fieldRule).mustMatch,
        };

      default:
        return undefined;
    }
  }

  private maxLength(field: FieldDefinitionModel) {
    if (!field.fieldRule) {
      return null;
    }

    let rule = BaseFieldRule.singleLineText(field.fieldRule).maxLength || null;

    if (this._fieldTypeRules[field.fieldTypeDefinitionId]) {
      rule =
        rule ||
        BaseFieldRule.singleLineText(
          this._fieldTypeRules[field.fieldTypeDefinitionId]
        ).maxLength;
    }

    return rule;
  }

  private minLength(field: FieldDefinitionModel) {
    if (!field.fieldRule) {
      return null;
    }

    let rule = BaseFieldRule.singleLineText(field.fieldRule).minLength || null;

    if (this._fieldTypeRules[field.fieldTypeDefinitionId]) {
      rule =
        rule ||
        BaseFieldRule.singleLineText(
          this._fieldTypeRules[field.fieldTypeDefinitionId]
        ).minLength;
    }

    return rule;
  }

  private pattern(field: FieldDefinitionModel) {
    if (!field.fieldRule) {
      return null;
    }

    let rule =
      BaseFieldRule.singleLineText(field.fieldRule).regexPattern || null;

    if (this._fieldTypeRules[field.fieldTypeDefinitionId]) {
      rule =
        rule ||
        BaseFieldRule.singleLineText(
          this._fieldTypeRules[field.fieldTypeDefinitionId]
        ).regexPattern;
    }

    return rule;
  }

  private getIncrementValuesList(
    additionalData: CustomDataAdditionalDataModel | undefined
  ): IncrementValuesListItem[] | undefined {
    return additionalData?.incrementValues
      ? Object.entries(additionalData.incrementValues)
          .map(([key, value]) => {
            return { display: key, value: +value };
          })
          .sort((a, b) => a.value - b.value)
      : undefined;
  }
}
